import { BlobImage } from "../media/BlobImage.tsx";
import { clockGlyphs } from "./clockGlyphs.ts";
import { lazy, Suspense } from "react";
const NativeScreenText = lazy(() => import("./screenText.tsx").then(module => ({ default: module.NativeScreenText })));
import styles from "./DeviceScreen.module.css";

type Props = {
  text?: string | null;
  image?: Blob | string | null;
  imageFit?: "contain" | "cover";
  mode?: "message" | "clock" | "pomodoro" | "empty";
  at?: Date;
  timezone?: string | null;
  remainingSec?: number;
  phase?: string;
  placeholder?: string;
  sender?: string | null;
};

const EMPTY_CLOCK = new Date(0);

/** Shared 320×240 geometry: the photo fills the screen, the outlined caption sits over its bottom. */
export function DeviceScreen({ text, image, imageFit = "contain", mode = "message", at = EMPTY_CLOCK, timezone, remainingSec = 1500, phase = "Фокус", sender, placeholder = "Текст и изображение появятся здесь" }: Props) {
  const caption = text?.trim();
  return <div className={`${styles.screen} ${image ? styles.photo : ""}`} data-screen-mode={mode} data-caption={Boolean(image && caption)}>
    {mode === "clock" ? <Clock at={at} timezone={timezone} /> : mode === "pomodoro" ? <div className={styles.pomodoro}><span>{phase}</span><strong>{Math.floor(remainingSec / 60).toString().padStart(2, "0")}:{(remainingSec % 60).toString().padStart(2, "0")}</strong></div> : image ? <>
      <div className={styles.viewport}>{typeof image === "string" ? <img className={styles.image} src={image} style={{ objectFit: imageFit }} alt="Фото на экране E-va" /> : <BlobImage className={styles.image} blob={image} style={{ objectFit: imageFit }} alt="Фото на экране E-va" />}</div>
      <Suspense fallback={null}><NativeScreenText text={caption ?? ""} photo sender={sender} /></Suspense>
    </> : caption ? <Suspense fallback={null}><NativeScreenText text={caption} photo={false} sender={sender} /></Suspense> : <div className={styles.empty}><span aria-hidden="true">✦</span><p>{placeholder}</p></div>}
  </div>;
}

function Clock({ at, timezone }: { at: Date; timezone?: string | null }) {
  let zone = "Etc/GMT-5";
  try { if (timezone) { new Intl.DateTimeFormat("ru", { timeZone: timezone }).format(at); zone = timezone; } } catch { /* Fall back to the browser's zone for older devices without a saved zone. */ }
  const time = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: zone }).format(at);
  const day = new Intl.DateTimeFormat("ru", { weekday: "long", timeZone: zone }).format(at);
  const date = new Intl.DateTimeFormat("ru", { day: "numeric", month: "short", timeZone: zone }).format(at).replace(/\./g, "");
  const glyphs = [...time].filter((c): c is keyof typeof clockGlyphs.glyphs => c in clockGlyphs.glyphs).map(c => clockGlyphs.glyphs[c]);
  const width = glyphs.reduce((sum, g) => sum + g.width, 0);
  let x = 0;
  return <><div className={styles.calendar}><span>{date}</span><span>{day}</span></div><svg className={styles.clock} viewBox={`0 0 ${width} ${clockGlyphs.height}`} preserveAspectRatio="none" role="img" aria-label={time}>{glyphs.map((g, i) => { const left = x; x += g.width; return <path key={i} transform={`translate(${left} 0)`} d={g.path} fill="currentColor" />; })}</svg><span className={styles.dot} /></>;
}
